// ------------------ IMPORTS -------------------
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { Info } from "luxon";

import { PassportPeriodType } from "#src/services/endpoint/passports/enums";

// ------------------ TYPES -------------------

interface UseTimeFrameLogicProps {
  timeFrameRepeatEvery: number;
  timeFrameType: PassportPeriodType;
  timeFrameRepeatOn: number[];
  setTimeFrameRepeatOn: (repeatOn: number[]) => void;
  timeFrameRepeatOnDate: number;
  setTimeFrameRepeatOnDate: (repeatOn: number) => void;
}

interface UseTimeFrameLogicReturn {
  weekDayOptions: Array<{ value: number; label: string }>;
  periodOptions: Array<{ value: PassportPeriodType; label: string }>;
  repeatEveryOptions: Array<{ value: number; label: string }>;
  handleWeekDayToggle: (dayValue: number) => void;
  handleMonthDayChange: (value: number) => void;
  verdictText: string;
}

// ------------------ HOOK -------------------

export const useTimeFrameLogic = ({
  timeFrameRepeatEvery,
  timeFrameType,
  timeFrameRepeatOn,
  setTimeFrameRepeatOn,
  timeFrameRepeatOnDate,
  setTimeFrameRepeatOnDate,
}: UseTimeFrameLogicProps): UseTimeFrameLogicReturn => {
  const { t, i18n } = useTranslation("endpoint", { keyPrefix: "passports" });

  // ------------------ DATA -------------------

  const weekDayOptions = Info.weekdays("short", { locale: i18n.language }).map(
    (day, index) => ({
      value: index,
      label: day,
    })
  );

  const weekDayOptionsFull = Info.weekdays("long", {
    locale: i18n.language,
  }).map((day, index) => ({
    value: index,
    label: day,
  }));

  const periodOptions = [
    {
      value: PassportPeriodType.DAILY,
      label: t("modal.addAndEdit.time_frame.period.daily"),
    },
    {
      value: PassportPeriodType.WEEKLY,
      label: t("modal.addAndEdit.time_frame.period.weekly"),
    },
    {
      value: PassportPeriodType.MONTHLY,
      label: t("modal.addAndEdit.time_frame.period.monthly"),
    },
  ];

  const repeatEveryOptions = useMemo(() => {
    const unit = timeFrameType === PassportPeriodType.WEEKLY ? "week" : "month";
    return Array.from({ length: 3 }, (_, i) => ({
      value: i + 1,
      label:
        `${i + 1} ` +
        t(`modal.addAndEdit.time_frame.${unit}${i > 0 ? "s" : ""}`),
    }));
  }, [timeFrameType, t]);

  // Verdict text based on time frame type and repeat options
  const verdictText = useMemo(() => {
    // Daily
    if (timeFrameType === PassportPeriodType.DAILY) {
      return t("modal.addAndEdit.time_frame.period.daily").toLowerCase();
    }

    // Weekly
    if (timeFrameType === PassportPeriodType.WEEKLY) {
      let res = t("modal.addAndEdit.time_frame.period.weekly").toLowerCase();
      if (timeFrameRepeatEvery > 1) {
        res =
          t("modal.addAndEdit.time_frame.verdict.every") +
          ` ${timeFrameRepeatEvery} ` +
          t(
            `modal.addAndEdit.time_frame.week${timeFrameRepeatEvery > 1 ? "s" : ""}`
          );
      }

      if (timeFrameRepeatOn.length > 0) {
        res += ` (${t("modal.addAndEdit.time_frame.verdict.on")} ${timeFrameRepeatOn
          .sort()
          .map(
            (day) => weekDayOptionsFull.find((opt) => opt.value === day)?.label
          )
          .join(", ")})`;
      }

      return res;
    }

    // Monthly
    if (timeFrameType === PassportPeriodType.MONTHLY) {
      let res = t("modal.addAndEdit.time_frame.period.monthly").toLowerCase();
      if (timeFrameRepeatEvery > 1) {
        res =
          t("modal.addAndEdit.time_frame.verdict.every") +
          ` ${timeFrameRepeatEvery} ` +
          t(
            `modal.addAndEdit.time_frame.month${timeFrameRepeatEvery > 1 ? "s" : ""}`
          );
      }

      if (timeFrameRepeatOnDate > 0) {
        res += ` (${t("modal.addAndEdit.time_frame.verdict.on")} ${t("modal.addAndEdit.time_frame.date_of_month", { date: timeFrameRepeatOnDate }).toLowerCase()})`;
      }

      return res;
    }

    return "";
  }, [
    timeFrameType,
    t,
    timeFrameRepeatEvery,
    timeFrameRepeatOn,
    timeFrameRepeatOnDate,
    weekDayOptionsFull,
  ]);

  // ------------------ METHODS -------------------

  const handleWeekDayToggle = (dayValue: number) => {
    const isSelected = timeFrameRepeatOn.includes(dayValue);
    if (isSelected) {
      setTimeFrameRepeatOn(timeFrameRepeatOn.filter((d) => d !== dayValue));
    } else {
      setTimeFrameRepeatOn([...timeFrameRepeatOn, dayValue]);
    }
  };

  const handleMonthDayChange = (value: number) => {
    if (!isNaN(value) && value >= 1 && value <= 31) {
      setTimeFrameRepeatOnDate(value);
    } else {
      setTimeFrameRepeatOnDate(0);
    }
  };

  // ------------------ EFFECTS -------------------

  // ------------------ RETURN -------------------

  return {
    weekDayOptions,
    periodOptions,
    repeatEveryOptions,
    handleWeekDayToggle,
    handleMonthDayChange,
    verdictText,
  };
};
